import React, { useState } from 'react'
import { Smartphone, Monitor } from 'lucide-react'

interface BasePreviewProps {
  children: React.ReactNode
  title: string
  isLoading?: boolean
  onExternalPreview?: () => void
  hideViewControls?: boolean
  hideZoomControls?: boolean
}

export const BasePreview: React.FC<BasePreviewProps> = ({ 
  children, 
  title, 
  isLoading = false,
  onExternalPreview,
  hideViewControls = false,
  hideZoomControls = false
}) => {
  const [viewMode, setViewMode] = useState<'desktop' | 'mobile' | 'tablet'>('mobile')
  const [zoom, setZoom] = useState(1) // Default zoom to 1 for consistency

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.1, 1.5))
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.1, 0.5))

  return (
    <div className="flex flex-col items-center justify-center min-h-full py-2">
      {/* View Mode Selector */}
      {!hideViewControls && (
        <div className="mb-2 flex items-center gap-2 bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => setViewMode('mobile')}
            className={`p-2 rounded ${viewMode === 'mobile' ? 'bg-white shadow-sm' : 'hover:bg-gray-50'}`}
            title="Mobile Ansicht"
          >
            <Smartphone className="w-4 h-4" />
          </button>
          <button
            onClick={() => setViewMode('tablet')}
            className={`p-2 rounded ${viewMode === 'tablet' ? 'bg-white shadow-sm' : 'hover:bg-gray-50'}`}
            title="Tablet Ansicht"
          >
            {/* Custom tablet icon that's more distinct from phone */}
            <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="1" y="2" width="14" height="12" stroke="currentColor" strokeWidth="1.5" rx="1"/>
              <circle cx="8" cy="13" r="0.5" fill="currentColor"/>
            </svg>
          </button>
        </div>
      )}

      <div className="relative">
        {viewMode === 'mobile' ? (
          /* Mobile View - Modern iPhone Frame */
          <div className="relative mx-auto rounded-[3rem] p-[3px] h-[618px] w-[300px] shadow-2xl"
               style={{
                 background: 'linear-gradient(to bottom right, #9CA3AF, #D1D5DB, #9CA3AF)'
               }}>
            {/* Inner black frame */}
            <div className="bg-black rounded-[2.85rem] p-[8px] w-full h-full relative">
              {/* Dynamic Island - adjusted dimensions and position */}
              <div className="absolute top-[16px] left-1/2 transform -translate-x-1/2 w-[89px] h-[28px] bg-black rounded-[14px] z-20 overflow-hidden">
                {/* Camera lens detail */}
                <div className="absolute right-[8px] top-1/2 transform -translate-y-1/2 w-[12px] h-[12px]">
                  <div className="absolute inset-0 bg-gray-900 rounded-full"></div>
                  <div className="absolute inset-[2px] bg-gray-800 rounded-full"></div>
                  <div className="absolute inset-[3px] bg-black rounded-full"></div>
                  <div className="absolute top-[3px] right-[3px] w-[2px] h-[2px] bg-blue-900 rounded-full opacity-50"></div>
                </div>
              </div>
              
              {/* Screen with scrollable content */}
              <div className="rounded-[2.3rem] overflow-hidden w-full h-full bg-white relative">
                {/* Status Bar */}
                <div className="absolute top-0 left-0 right-0 z-10 bg-transparent pt-[14px] pb-1">
                  <div className="flex justify-between items-center">
                    <span className="text-[11.5px] font-semibold text-black ml-9">9:41</span>
                    <div className="flex items-center gap-1 mr-5">
                      {/* Cellular Signal */}
                      <svg className="w-3.5 h-2.5" viewBox="0 0 17 12" fill="black">
                        <rect x="1" y="6" width="3" height="6" rx="0.5"/>
                        <rect x="6" y="4" width="3" height="8" rx="0.5"/>
                        <rect x="11" y="2" width="3" height="10" rx="0.5"/>
                        <rect x="16" y="0" width="3" height="12" rx="0.5" opacity="0.3"/>
                      </svg>
                      {/* WiFi */}
                      <svg className="w-3.5 h-2.5" viewBox="0 0 15 11" fill="black">
                        <path fillRule="evenodd" clipRule="evenodd" d="M7.5 2.5C9.77817 2.5 11.867 3.35876 13.4149 4.75343L14.5 3.66829C12.6409 1.97761 10.1758 1 7.5 1C4.82416 1 2.35909 1.97761 0.5 3.66829L1.58507 4.75343C3.13305 3.35876 5.22183 2.5 7.5 2.5ZM7.5 5.5C8.91962 5.5 10.21 6.06817 11.1516 6.98374L12.2367 5.89861C10.9834 4.68326 9.32608 4 7.5 4C5.67392 4 4.01661 4.68326 2.76329 5.89861L3.84837 6.98374C4.78996 6.06817 6.08038 5.5 7.5 5.5ZM9.03033 8.46967C8.55086 8.17453 8.04301 8 7.5 8C6.95699 8 6.44914 8.17453 5.96967 8.46967L7.5 10L9.03033 8.46967Z"/>
                      </svg>
                      {/* Battery */}
                      <svg className="w-5 h-2.5" viewBox="0 0 25 12" fill="none">
                        <rect x="1" y="2" width="19" height="8" stroke="black" strokeWidth="1" rx="2" fill="none"/>
                        <rect x="2" y="3" width="17" height="6" fill="black" rx="1"/>
                        <path d="M21 4V8C21.7 8 22.5 7.3284 22.5 6C22.5 4.6716 21.7 4 21 4Z" fill="black"/>
                      </svg>
                    </div>
                  </div>
                </div>
                
                {/* Content area - no padding, colors extend to edges */}
                <div className="w-full h-full overflow-y-auto scrollbar-hide pt-[30px]">
                  {isLoading ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  ) : (
                    children
                  )}
                </div>
                
                {/* Home Indicator */}
                <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 w-[134px] h-[5px] bg-black rounded-full"></div>
              </div>
            </div>
            
            {/* iPhone Side Buttons */}
            <div className="absolute top-0 inset-x-0">
              {/* Silent Switch */}
              <div className="h-[32px] w-[3px] bg-gray-400 absolute left-[-5px] top-[74px] rounded-l-lg"></div>
              {/* Volume Up */}
              <div className="h-[46px] w-[3px] bg-gray-400 absolute left-[-5px] top-[126px] rounded-l-lg"></div>
              {/* Volume Down */}
              <div className="h-[46px] w-[3px] bg-gray-400 absolute left-[-5px] top-[180px] rounded-l-lg"></div>
              {/* Power Button */}
              <div className="h-[64px] w-[3px] bg-gray-400 absolute right-[-5px] top-[144px] rounded-r-lg"></div>
            </div>
          </div>
        ) : viewMode === 'tablet' ? (
          /* Tablet View - iPad Frame */
          <div className="relative mx-auto border-[10px] border-gray-800 bg-gray-800 rounded-[2.5rem] h-[600px] w-[450px] shadow-xl">
            {/* Camera notch */}
            <div className="absolute top-[15px] left-1/2 transform -translate-x-1/2 w-[12px] h-[12px] bg-black rounded-full z-20"></div>
            
            {/* Screen with scrollable content */}
            <div className="rounded-[2rem] overflow-hidden w-full h-full bg-white relative">
              {/* Status Bar */}
              <div className="absolute top-0 left-0 right-0 h-[32px] bg-white z-10 flex items-center justify-between px-5 pt-1">
                <span className="text-xs font-medium text-gray-900">9:41 AM</span>
                <div className="flex items-center gap-1">
                  {/* WiFi */}
                  <svg className="w-4 h-3" viewBox="0 0 16 12" fill="none">
                    <path d="M8 11.5C8.41421 11.5 8.75 11.1642 8.75 10.75C8.75 10.3358 8.41421 10 8 10C7.58579 10 7.25 10.3358 7.25 10.75C7.25 11.1642 7.58579 11.5 8 11.5Z" fill="#000"/>
                    <path d="M5.17071 7.67071C5.56124 7.28019 6.19444 7.28019 6.58496 7.67071L8 9.08575L9.41504 7.67071C9.80556 7.28019 10.4388 7.28019 10.8293 7.67071C11.2198 8.06124 11.2198 8.69444 10.8293 9.08496L8.70711 11.2071C8.31658 11.5976 7.68342 11.5976 7.29289 11.2071L5.17071 9.08496C4.78019 8.69444 4.78019 8.06124 5.17071 7.67071Z" fill="#000"/>
                    <path d="M2.34315 4.84315C2.73367 4.45262 3.36683 4.45262 3.75736 4.84315L8 9.08579L12.2426 4.84315C12.6332 4.45262 13.2663 4.45262 13.6569 4.84315C14.0474 5.23367 14.0474 5.86683 13.6569 6.25736L8.70711 11.2071C8.31658 11.5976 7.68342 11.5976 7.29289 11.2071L2.34315 6.25736C1.95262 5.86683 1.95262 5.23367 2.34315 4.84315Z" fill="#000" opacity="0.3"/>
                    <path d="M0.222183 2.72218C0.612708 2.33166 1.24587 2.33166 1.6364 2.72218L8 9.08579L14.3636 2.72218C14.7541 2.33166 15.3873 2.33166 15.7778 2.72218C16.1683 3.11271 16.1683 3.74587 15.7778 4.1364L8.70711 11.2071C8.31658 11.5976 7.68342 11.5976 7.29289 11.2071L0.222183 4.1364C-0.168343 3.74587 -0.168343 3.11271 0.222183 2.72218Z" fill="#000" opacity="0.15"/>
                  </svg>
                  {/* Battery */}
                  <svg className="w-6 h-3" viewBox="0 0 25 12" fill="none">
                    <rect x="1" y="2" width="19" height="8" stroke="#000" strokeWidth="1" rx="2" fill="none"/>
                    <rect x="2" y="3" width="17" height="6" fill="#000" rx="1"/>
                    <path d="M21 4.5V7.5C22.1046 7.5 23 6.82843 23 6C23 5.17157 22.1046 4.5 21 4.5Z" fill="#000"/>
                  </svg>
                </div>
              </div>
              
              {/* Content area */}
              <div className="w-full h-full pt-[32px] overflow-y-auto scrollbar-hide">
                {isLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : (
                  children
                )}
              </div>
            </div>
            
            {/* iPad Side Buttons */}
            <div className="absolute top-0 inset-x-0">
              {/* Power Button */}
              <div className="h-[50px] w-[4px] bg-gray-600 absolute right-[-14px] top-[50px] rounded-r-lg"></div>
              {/* Volume Up */}
              <div className="h-[35px] w-[4px] bg-gray-600 absolute right-[-14px] top-[120px] rounded-r-lg"></div>
              {/* Volume Down */}
              <div className="h-[35px] w-[4px] bg-gray-600 absolute right-[-14px] top-[165px] rounded-r-lg"></div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}